import dayjs from "dayjs";
import { GENERAL_ORDER_DISPLAY_LABEL } from "./operationRequestConstants.js";

export const STATUS_FILTER_ALL = "all";
export const STATUS_FILTER_OPEN = "open";
export const STATUS_FILTER_IN_PROGRESS = "in_progress";
export const STATUS_FILTER_WAITING_PAYMENT = "waiting_payment";
export const STATUS_FILTER_CLOSED = "closed";

export const STATUS_FILTER_OPTIONS = [
  { value: STATUS_FILTER_ALL, label: "הכל" },
  { value: STATUS_FILTER_OPEN, label: "פתוח" },
  { value: STATUS_FILTER_IN_PROGRESS, label: "בטיפול" },
  { value: STATUS_FILTER_WAITING_PAYMENT, label: "ממתין לתשלום" },
  { value: STATUS_FILTER_CLOSED, label: "נסגר" },
];

export const STATUS_TAG_COLORS = {
  open: "green",
  in_progress: "processing",
  waiting_payment: "orange",
  waiting_for_payment: "orange",
  paid: "cyan",
  completed: "success",
  rejected: "error",
  cancelled: "default",
  closed: "default",
};

export const UNAVAILABLE = "לא זמין";

export function pickField(item, ...keys) {
  if (!item || typeof item !== "object") return null;
  for (const k of keys) {
    const v = item[k];
    if (v != null && String(v).trim() !== "") return String(v).trim();
  }
  return null;
}

export function displayOrUnavailable(value) {
  const s = value != null ? String(value).trim() : "";
  return s || UNAVAILABLE;
}

export function shortRequestIdFromItem(item) {
  return (
    pickField(item, "shortRequestId", "short_request_id") ||
    (pickField(item, "id") ? String(item.id).replace(/-/g, "").slice(0, 8).toUpperCase() : null)
  );
}

export function displayOrderNumberFromItem(item) {
  const display = pickField(item, "displayOrderNumber", "display_order_number");
  if (display) return display;
  const orderId = pickField(item, "orderId", "order_id");
  if (orderId) return orderId;
  return GENERAL_ORDER_DISPLAY_LABEL;
}

export function customerNameFromItem(item) {
  return pickField(
    item,
    "customerName",
    "customer_name",
    "mainContact",
    "main_contact",
    "fullname",
    "clientName",
    "client_name"
  );
}

export function hotelFromItem(item) {
  return pickField(item, "hotelOrDestination", "hotel_or_destination");
}

export function requestTypeLabelFromItem(item) {
  return pickField(item, "requestTypeLabel", "request_type_label", "requestType", "request_type");
}

/** Public site → Ops contact (created_by marker or display label). */
export function publicOpsContactSourceFromItem(item) {
  const createdBy = pickField(item, "createdBy", "created_by");
  const display = pickField(item, "displayOrderNumber", "display_order_number");
  const message = pickField(item, "message", "latestMessagePreview", "latest_message_preview") || "";
  const isPublic =
    createdBy === "public_ops_contact" ||
    display === "מתעניין" ||
    display === "HotelsRiver Business" ||
    message.includes("מקור פנייה:");
  if (!isPublic) return null;
  if (display === "HotelsRiver Business" || message.includes("HotelsRiver Business")) {
    return {
      channel: "business",
      label: "HotelsRiver Business",
      color: "purple",
    };
  }
  return {
    channel: "interested",
    label: "מתעניין (מחוץ למערכת)",
    color: "magenta",
  };
}

export function statusKeyFromItem(item) {
  const effective = pickField(item, "effectiveStatus", "effective_status");
  if (effective) return effective;
  const status = pickField(item, "status") || "open";
  const paymentRequired = Boolean(item.paymentRequired ?? item.payment_required);
  const paymentStatus = pickField(item, "paymentStatus", "payment_status");
  if (paymentRequired && paymentStatus === "pending" && status !== "closed") {
    return STATUS_FILTER_WAITING_PAYMENT;
  }
  return status;
}

export function statusLabelFromItem(item) {
  const fromApi = pickField(item, "statusLabel", "status_label");
  if (fromApi) return fromApi;
  const key = statusKeyFromItem(item);
  const fromFilter = STATUS_FILTER_OPTIONS.find((o) => o.value === key)?.label;
  if (fromFilter) return fromFilter;
  const fromAdmin = {
    waiting_for_payment: "ממתין לתשלום",
    paid: "שולם",
    completed: "הושלם",
    rejected: "נדחה",
    cancelled: "בוטל",
  }[key];
  return fromAdmin || key;
}

export function latestMessageFromItem(item) {
  return pickField(
    item,
    "latestMessagePreview",
    "latest_message_preview",
    "message"
  );
}

export function lastMessageSenderTypeFromItem(item) {
  return String(
    pickField(item, "lastMessageSenderType", "last_message_sender_type") || ""
  )
    .trim()
    .toLowerCase();
}

export function hasUnreadAdminReplyFromItem(item) {
  if (!item || typeof item !== "object") return false;
  return Boolean(item.hasUnreadAdminReply || item.has_unread_admin_reply);
}

export function hasUnreadAgentReplyFromItem(item) {
  if (!item || typeof item !== "object") return false;
  return Boolean(item.hasUnreadAgentReply || item.has_unread_agent_reply);
}

export function formatDateTime(iso) {
  if (!iso) return UNAVAILABLE;
  const d = dayjs(iso);
  return d.isValid() ? d.format("DD/MM/YYYY HH:mm") : UNAVAILABLE;
}

export function matchesStatusFilter(item, filterId) {
  if (!filterId || filterId === STATUS_FILTER_ALL) return true;
  return statusKeyFromItem(item) === filterId;
}

export function matchesSearchQuery(item, query) {
  const q = String(query ?? "").trim().toLowerCase();
  if (!q) return true;
  const hay = [
    displayOrderNumberFromItem(item),
    customerNameFromItem(item),
    hotelFromItem(item),
    shortRequestIdFromItem(item),
    pickField(item, "id"),
    pickField(item, "message"),
    latestMessageFromItem(item),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
}

export function matchesOrderId(item, orderId) {
  const oid = String(orderId ?? "").trim();
  if (!oid) return true;
  const candidates = [
    pickField(item, "orderId", "order_id"),
    pickField(item, "displayOrderNumber", "display_order_number"),
  ].filter(Boolean);
  return candidates.includes(oid);
}

export function filterAgentOperationRequests(items, { statusFilter, searchQuery, orderId }) {
  const list = Array.isArray(items) ? items : [];
  return list.filter(
    (item) =>
      matchesStatusFilter(item, statusFilter) &&
      matchesSearchQuery(item, searchQuery) &&
      matchesOrderId(item, orderId)
  );
}
